'use client';

import { createRoot, type Root } from 'react-dom/client';

import { NewsletterForm } from '@/features/forms/components/newsletter';
import type { NewsletterFormValues } from '@/features/forms/components/newsletter';
import { createWidgetMount } from '@/shared/utils/widget-mount';

import {
  OBSERVED_NEWSLETTER_WIDGET_ATTRIBUTES,
  readNewsletterWidgetProps,
} from './newsletter-element-props';
import type { NewsletterEventName } from './newsletter-element-props';
import { submitNewsletter } from '@/features/forms/components/newsletter/submitNewsletter';

function dispatchNewsletterEvent(
  element: HTMLElement,
  name: NewsletterEventName,
  detail?: unknown,
): void {
  element.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail,
    }),
  );
}

export class NewsletterWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_NEWSLETTER_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-newsletter-widget-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readNewsletterWidgetProps(this);

    dispatchNewsletterEvent(this, 'adipa-newsletter-ready', {
      submitUrl: props.submitUrl,
    });

    this.root.render(
      <NewsletterForm
        variant="standalone"
        previewScenario={props.previewScenario}
        onSubmit={
          props.submitUrl
            ? async (values: NewsletterFormValues) => {
                dispatchNewsletterEvent(this, 'adipa-newsletter-submit', values);
                await submitNewsletter({
                  pageUrl: props.pageUrl,
                  recaptchaAction: props.recaptchaAction,
                  recaptchaSiteKey: props.recaptchaSiteKey,
                  source: props.source,
                  url: props.submitUrl!,
                  values,
                });
              }
            : (values: NewsletterFormValues) => {
                dispatchNewsletterEvent(this, 'adipa-newsletter-submit', values);
              }
        }
        onSuccess={(values: NewsletterFormValues) => {
          dispatchNewsletterEvent(this, 'adipa-newsletter-success', values);
        }}
        onError={(error: unknown) => {
          dispatchNewsletterEvent(this, 'adipa-newsletter-error', {
            error: error instanceof Error ? error.message : String(error),
          });
        }}
      />,
    );
  }
}
